import {useEffect, useRef, useState} from "react";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {RiderPreSettlement, riderPreSettlementListFromDto} from "../types/riderPreSettlement.types";
import {riderPreSettlementApi} from "../api/riderPreSettlementApi";
import { formatDateToDisplay } from "@/shared/utils/formatter";
import {supplierApi} from "@/features/partner/supplierStatus/api/SupplierApi";
import {Supplier, supplierListFromDto} from "@/features/partner/supplierStatus/types/supplier.types";

/**
 * 라이더 선정산내역 데이터 관리 훅 (업로드 기능 없음)
 *
 * @param userSupplierId - 협력사 사용자 ID (협력사 로그인 시)
 * @param skipInitialFetch - 초기 자동 조회 건너뛰기 여부
 */
export const useRiderPreSettlementData = (userSupplierId?: string | null, skipInitialFetch = false) => {
    const { showToast } = useToast();
    const [settlements, setSettlements] = useState<RiderPreSettlement[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [latestDate, setLatestDate] = useState<string>('');
    
    // 각 데이터 로딩 상태를 개별적으로 관리
    const [suppliersLoaded, setSuppliersLoaded] = useState(false);
    const [latestDateLoaded, setLatestDateLoaded] = useState(false);
    
    // 전체 로딩 상태 계산
    const loading = !suppliersLoaded || !latestDateLoaded;

    const hasFetchedSuppliers = useRef(false);
    const hasFetchedLatestDate = useRef(false);

    // 협력사 목록 조회
    useEffect(() => {
        if (hasFetchedSuppliers.current) return;

        let isCancelled = false;

        const fetchSuppliers = async () => {
            try {
                const response = await supplierApi.getSupplierList();
                if (!isCancelled) {
                    const supplierList = supplierListFromDto(response);
                    setSuppliers(supplierList.suppliers);
                    hasFetchedSuppliers.current = true;
                    setSuppliersLoaded(true);
                }
            } catch {
                if (!isCancelled) {
                    showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
                    setSuppliersLoaded(true); // 에러여도 로딩은 완료 처리
                }
            }
        };

        fetchSuppliers();
        return () => {
            isCancelled = true;
        };
    }, [showToast]);

    // 최근 선정산종료일자 조회
    useEffect(() => {
        if (hasFetchedLatestDate.current) return;

        // 협력사 사용자인 경우: userSupplierId가 있어야 조회
        // 본사 사용자인 경우: suppliers가 로드되어야 조회 (userSupplierId가 null인 경우)
        if (userSupplierId) {
            // 협력사 사용자는 바로 조회 가능
        } else if (userSupplierId === null && (!suppliersLoaded || suppliers.length === 0)) {
            // 본사 사용자는 suppliers 로드 대기
            return;
        }

        let isCancelled = false;

        const fetchLatestDate = async () => {
            try {
                // 협력사 ID를 전달하여 해당 협력사의 최신 날짜 조회
                const supplierId = userSupplierId || (suppliers.length > 0 ? suppliers[0]?.id : undefined);
                const response = await riderPreSettlementApi.getLatestSettlementDate(supplierId);
                if (!isCancelled) {
                    // 배열 형태로 오는 경우 대비 포맷팅
                    const formattedDate = formatDateToDisplay(response.latestEndDate);
                    setLatestDate(formattedDate);
                    hasFetchedLatestDate.current = true;
                    setLatestDateLoaded(true);
                }
            } catch {
                if (!isCancelled) {
                    // 에러가 발생해도 빈 문자열로 처리 (데이터가 없을 수 있음)
                    setLatestDate('');
                    hasFetchedLatestDate.current = true;
                    setLatestDateLoaded(true); // 에러여도 로딩은 완료 처리
                }
            }
        };

        fetchLatestDate();
        return () => {
            isCancelled = true;
        };
    }, [showToast, userSupplierId, suppliers, suppliersLoaded]);

    // skipInitialFetch가 true이면 컴포넌트에서 직접 조회하므로 이 useEffect는 실행 안 함
    // (현재는 항상 skipInitialFetch=true로 설정되어 있음)

    /**
     * 최신 선정산종료일자 재조회
     */
    const refreshLatestDate = async (): Promise<string | null> => {
        try {
            const response = await riderPreSettlementApi.getLatestSettlementDate();
            // 배열 형태로 오는 경우 대비 포맷팅
            const formattedDate = formatDateToDisplay(response.latestEndDate);
            setLatestDate(formattedDate);
            return formattedDate;
        } catch {
            return null;
        }
    }

    /**
     * 선정산내역 재조회
     * @returns 조회된 정산내역 건수 (에러 시 -1)
     */
    const refreshSettlements = async (
        acctStDate: string,
        acctEdDate: string,
        riderName?: string,
        supplierId?: string
    ): Promise<number> => {
        try {
            const response = await riderPreSettlementApi.getRiderPreSettlementList(
                acctStDate,
                acctEdDate,
                riderName,
                supplierId
            );
            const settlementList = riderPreSettlementListFromDto(response);
            setSettlements(settlementList);
            return settlementList.length;
        } catch {
            showToast('선정산내역을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            return -1;
        }
    };

    /**
     * 엑셀 다운로드
     */
    const downloadExcel = async (
        acctStDate: string,
        acctEdDate: string,
        riderName?: string,
        supplierId?: string
    ): Promise<void> => {
        try {
            const blob = await riderPreSettlementApi.downloadExcel(
                acctStDate,
                acctEdDate,
                riderName,
                supplierId
            );

            // 파일명 생성 (YYYYMMDD 형식으로 변환)
            const startDateFormatted = acctStDate.replace(/-/g, '');
            const endDateFormatted = acctEdDate.replace(/-/g, '');
            const fileName = `${startDateFormatted}~${endDateFormatted}_라이더선정산내역.xlsx`;

            // Blob을 다운로드
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('엑셀 다운로드가 완료되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    return {
        settlements,
        suppliers,
        latestDate,
        loading,
        refreshLatestDate,
        refreshSettlements,
        downloadExcel
    };
};
